import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactCopyButtonRefs
 * @property {HTMLButtonElement} button - Button that copies the text
 * @property {HTMLElement} label - Visible button label
 * @property {HTMLElement} status - Live region that announces the copy
 */

/**
 * Copies the `text-to-copy` attribute to the clipboard, briefly swaps the
 * button label to the `copied-label` attribute and announces it to screen readers.
 *
 * @extends {Component<ContactCopyButtonRefs>}
 */
class ContactCopyButton extends Component {
  requiredRefs = ['button', 'label', 'status'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {string} */
  #defaultLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#defaultLabel = this.refs.label.textContent ?? '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimeout);
  }

  async copy() {
    const text = this.getAttribute('text-to-copy');

    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return;
    }

    const copiedLabel = this.getAttribute('copied-label') ?? '';

    this.refs.label.textContent = copiedLabel;
    this.refs.status.textContent = copiedLabel;

    clearTimeout(this.#resetTimeout);
    this.#resetTimeout = window.setTimeout(() => {
      this.refs.label.textContent = this.#defaultLabel;
      this.refs.status.textContent = '';
    }, 2000);
  }
}

if (!customElements.get('contact-copy-button')) {
  customElements.define('contact-copy-button', ContactCopyButton);
}
